/**
 * Theme provider — light/dark with persistence.
 * Mirrors the design: persists to localStorage('skbill-theme') and reflects
 * the choice on <html data-theme> for web (so the FOUC script + any CSS stay
 * in sync). On native it just drives the in-memory theme.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Theme, buildTheme, lightTheme } from '../theme/theme';

type ThemeName = 'light' | 'dark';
const STORAGE_KEY = 'skbill-theme';

interface ThemeContextValue {
  theme: Theme;
  isDark: boolean;
  themeName: ThemeName;
  toggleTheme: () => void;
  setTheme: (name: ThemeName) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function applyWebAttr(name: ThemeName) {
  if (Platform.OS === 'web' && typeof document !== 'undefined') {
    document.documentElement.setAttribute('data-theme', name);
  }
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [themeName, setThemeName] = useState<ThemeName>('light');

  useEffect(() => {
    (async () => {
      try {
        const stored = (await AsyncStorage.getItem(STORAGE_KEY)) as ThemeName | null;
        if (stored === 'light' || stored === 'dark') {
          setThemeName(stored);
          applyWebAttr(stored);
        }
      } catch {
        /* ignore */
      }
    })();
  }, []);

  const setTheme = useCallback((name: ThemeName) => {
    setThemeName(name);
    applyWebAttr(name);
    AsyncStorage.setItem(STORAGE_KEY, name).catch(() => {});
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeName((prev) => {
      const next: ThemeName = prev === 'light' ? 'dark' : 'light';
      applyWebAttr(next);
      AsyncStorage.setItem(STORAGE_KEY, next).catch(() => {});
      return next;
    });
  }, []);

  const value = useMemo<ThemeContextValue>(() => {
    const isDark = themeName === 'dark';
    return { theme: buildTheme(isDark), isDark, themeName, toggleTheme, setTheme };
  }, [themeName, toggleTheme, setTheme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

/**
 * Forces a fixed light ("white") theme for everything rendered inside it,
 * ignoring the global light/dark setting. Used for the auth screens, which
 * must always render in white mode. The real toggle/setTheme are still
 * forwarded so the global preference stays intact after auth.
 */
export function ForceLightTheme({ children }: { children: React.ReactNode }) {
  const parent = useContext(ThemeContext);
  const value = useMemo<ThemeContextValue>(
    () => ({
      theme: lightTheme,
      isDark: false,
      themeName: 'light',
      toggleTheme: parent?.toggleTheme ?? (() => {}),
      setTheme: parent?.setTheme ?? (() => {}),
    }),
    [parent],
  );
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
